import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { MoodId } from '../components/moodmate/moods';
import * as moodService from '../services/moodService';
import { applyMotionPref } from '../components/moodmate/visual/motion';

/**
 * MoodMate state — optional, privacy-first mood tracking over time.
 *
 * ── Two storage modes, one surface ───────────────────────────────────────────
 * Persistence lives behind `services/moodService`, which chooses at runtime:
 *   • LOCAL (default / demo): localStorage keyed by email — the original
 *     on-device behaviour, and the offline/dev path. Active until the backend is
 *     deployed and `REACT_APP_MOOD_BACKEND=true` is set.
 *   • BACKEND: the same user's history on AppSync/DynamoDB, so it follows them
 *     across the website and the mobile app. A local cache backs offline reads.
 * This context's public shape is identical in both modes, so the dashboard,
 * journal, charts and settings never change.
 *
 * ── The canonical store is `checkIns` ────────────────────────────────────────
 * A check-in is one timestamped record: a mood plus optional journal text, tags,
 * energy and notes. Multiple per day are kept, never merged or overwritten, and
 * every weekly/monthly summary is COMPUTED from these records — never a stored
 * counter. Reflections (free-text answers to weekly/monthly prompts) are kept
 * separately so they never pollute the automatic summaries.
 *
 * ── Consent rules baked in ───────────────────────────────────────────────────
 * - `saveHistory` governs ALL persistence. With it off, choosing a mood still
 *   personalises the session, but nothing is written (locally or remotely).
 * - Existing on-device history is only ever uploaded by an explicit import, never
 *   silently. Every record and reflection is individually deletable; history and
 *   reflections are each clearable wholesale (locally and, in backend mode,
 *   remotely).
 * - The current session's mood lives in sessionStorage, forgotten when the
 *   session ends (and re-prompted next time, if prompts are on).
 */

export const MOOD_TAGS = ['work', 'family', 'health', 'study', 'personal'] as const;
export type MoodTag = typeof MOOD_TAGS[number];

export interface MoodCheckInRecord {
  id: string;
  mood: MoodId;
  createdAt: string; // ISO instant
  journalEntry?: string;
  tags?: MoodTag[];
  energyLevel?: number; // 1–5
  notes?: string;
}

export interface MoodReflection {
  id: string;
  period: 'week' | 'month';
  /** weekKey (that week's Monday 'YYYY-MM-DD') or monthKey ('YYYY-MM'). */
  periodKey: string;
  prompt: string;
  text: string;
  updatedAt: string;
}

export interface MoodPreferences {
  promptsEnabled: boolean;
  saveHistory: boolean;
  skipOnOpen: boolean;
}

export interface NewCheckInInput {
  mood: MoodId;
  journalEntry?: string;
  tags?: MoodTag[];
  energyLevel?: number;
  notes?: string;
}

/** Additive status for loading / offline / error handling. Consumers may ignore it. */
export interface MoodSyncStatus {
  /** Using the deployed backend (vs on-device localStorage). */
  backend: boolean;
  /** Initial history load in flight. */
  loading: boolean;
  /** Backend reachable? false after a failed call / browser offline. */
  offline: boolean;
  /** Last sync error message, if any (kept human-readable, never mood content). */
  error: string | null;
}

interface MoodContextType {
  sessionMood: MoodId | null;
  preferences: MoodPreferences;
  checkIns: MoodCheckInRecord[];
  reflections: MoodReflection[];
  needsCheckIn: boolean;
  status: MoodSyncStatus;
  /** Quick path (dashboard): set the mood and log a bare check-in (optimistic). */
  chooseMood: (mood: MoodId) => void;
  /**
   * Gate path: attempt to save a check-in and report whether it persisted. Commits
   * locally only on success, so a failed backend save is never shown as success.
   */
  submitCheckIn: (input: NewCheckInInput) => Promise<boolean>;
  /** Full path (Today composer): log a check-in with optional details (optimistic). */
  addCheckIn: (input: NewCheckInInput) => void;
  updateCheckIn: (id: string, patch: Partial<Omit<MoodCheckInRecord, 'id' | 'createdAt'>>) => void;
  deleteCheckIn: (id: string) => void;
  /** Upsert a reflection for a (period, periodKey, prompt). Empty text deletes it. */
  saveReflection: (period: 'week' | 'month', periodKey: string, prompt: string, text: string) => void;
  deleteReflection: (id: string) => void;
  skipCheckIn: () => void;
  updatePreferences: (partial: Partial<MoodPreferences>) => void;
  clearHistory: () => void;
  clearReflections: () => void;
  /** How many on-device records could be imported into the backend. */
  localHistoryCount: number;
  /** True when backend mode is on, local history exists, and it hasn't been imported. */
  canImportLocalHistory: boolean;
  /** Explicit, consented one-off upload of on-device history. Idempotent. */
  importLocalHistory: () => Promise<moodService.ImportResult>;
}

const DEFAULT_PREFS: MoodPreferences = {
  promptsEnabled: true,
  // On by default so the journal/insights are meaningful — but clearly disclosed,
  // and one toggle away from off with a wholesale clear.
  saveHistory: true,
  skipOnOpen: false,
};

const MoodContext = createContext<MoodContextType | undefined>(undefined);

// ── Storage helpers for the two stores the context still owns directly ───────
// Preferences are a device-level setting (not synced); the session gate is per
// tab. History/reflections are delegated to moodService.
const NS = 'creyente:moodmate';
const prefsKey = (email: string) => `${NS}:prefs:v1:${email}`;
const sessionKey = (email: string) => `${NS}:session:v1:${email}`;

function readJSON<T>(storage: Storage, key: string, fallback: T): T {
  try {
    const raw = storage.getItem(key);
    return raw ? ({ ...fallback, ...JSON.parse(raw) } as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeJSON(storage: Storage, key: string, value: unknown): void {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — degrade to in-memory, which is acceptable */
  }
}

interface SessionState {
  checkedIn: boolean;
  mood: MoodId | null;
}

const makeId = (p: string): string => `${p}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Build a check-in record from input, omitting empty optional fields. */
const buildRecord = (input: NewCheckInInput): MoodCheckInRecord => ({
  id: makeId('ci'),
  mood: input.mood,
  createdAt: new Date().toISOString(),
  ...(input.journalEntry?.trim() ? { journalEntry: input.journalEntry.trim() } : {}),
  ...(input.tags?.length ? { tags: input.tags } : {}),
  ...(input.energyLevel ? { energyLevel: input.energyLevel } : {}),
  ...(input.notes?.trim() ? { notes: input.notes.trim() } : {}),
});

export const MoodProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const email = user?.email || '';
  const backend = moodService.moodBackendEnabled();

  const [preferences, setPreferences] = useState<MoodPreferences>(DEFAULT_PREFS);
  const [checkIns, setCheckIns] = useState<MoodCheckInRecord[]>([]);
  const [reflections, setReflections] = useState<MoodReflection[]>([]);
  const [session, setSession] = useState<SessionState>({ checkedIn: false, mood: null });
  const [loading, setLoading] = useState<boolean>(false);
  const [offline, setOffline] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [imported, setImported] = useState<boolean>(false);

  // Set the MoodMate motion mode on <html> as early as the provider mounts, so the
  // first MoodMate paint already reflects the user's animation preference.
  useEffect(() => {
    applyMotionPref();
  }, []);

  // Guards writes against landing under '' or the previous user after a switch.
  const loadedEmail = useRef<string>('');
  // Read current saveHistory inside callbacks without re-creating them each change.
  const saveHistoryRef = useRef<boolean>(DEFAULT_PREFS.saveHistory);
  saveHistoryRef.current = preferences.saveHistory;
  // Monotonic token so a slow load for a previous user can't overwrite a newer one.
  const loadSeq = useRef(0);

  // Load everything for the signed-in user (history/reflections via the service).
  useEffect(() => {
    if (!email) {
      loadedEmail.current = '';
      loadSeq.current += 1;
      setPreferences(DEFAULT_PREFS);
      setCheckIns([]);
      setReflections([]);
      setSession({ checkedIn: false, mood: null });
      setLoading(false);
      setError(null);
      setImported(false);
      return;
    }
    loadedEmail.current = email;
    const seq = ++loadSeq.current;

    setPreferences(readJSON<MoodPreferences>(localStorage, prefsKey(email), DEFAULT_PREFS));
    setSession(readJSON<SessionState>(sessionStorage, sessionKey(email), { checkedIn: false, mood: null }));
    setImported(moodService.hasImported(email));
    setError(null);
    setLoading(true);

    Promise.all([moodService.loadCheckIns(email), moodService.loadReflections(email)])
      .then(([ci, rf]) => {
        if (seq !== loadSeq.current) return; // a newer load superseded this one
        setCheckIns(ci);
        setReflections(rf);
        setOffline(false);
      })
      .catch(() => {
        if (seq !== loadSeq.current) return;
        setOffline(true);
        setError('Could not reach the mood service. Showing what’s saved on this device.');
      })
      .finally(() => {
        if (seq === loadSeq.current) setLoading(false);
      });
  }, [email]);

  // Cross-tab sync for the LOCAL-mode store and preferences.
  useEffect(() => {
    if (!email) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === prefsKey(email)) setPreferences(readJSON(localStorage, prefsKey(email), DEFAULT_PREFS));
      if (!backend && e.key === `${NS}:checkins:v1:${email}`) setCheckIns(moodService.readLocalCheckIns(email));
      if (!backend && e.key === `${NS}:reflections:v1:${email}`) setReflections(moodService.readLocalReflections(email));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [email, backend]);

  // Reflect the browser's connectivity in the status (backend mode only).
  useEffect(() => {
    if (!backend) return;
    const update = () => setOffline(!navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, [backend]);

  const persistSession = useCallback(
    (next: SessionState) => {
      setSession(next);
      if (email) writeJSON(sessionStorage, sessionKey(email), next);
    },
    [email]
  );

  const flagRemoteError = useCallback(() => {
    setOffline(true);
    setError('Your latest change is saved on this device and will sync when the connection returns.');
  }, []);

  const addCheckIn = useCallback(
    (input: NewCheckInInput) => {
      persistSession({ checkedIn: true, mood: input.mood });
      if (!saveHistoryRef.current || !email) return; // consent: no record when history is off
      const record = buildRecord(input);
      setCheckIns((prev) => {
        const next = [record, ...prev].slice(0, 2000);
        moodService.cacheCheckIns(email, next);
        return next;
      });
      // Optimistic: the record is already shown and cached; a failed/unreachable
      // backend only flags the status (it stays as a pending offline write).
      moodService
        .createCheckIn(email, record)
        .then((ok) => {
          if (!ok) flagRemoteError();
        })
        .catch(flagRemoteError);
    },
    [persistSession, email, flagRemoteError]
  );

  const chooseMood = useCallback((mood: MoodId) => addCheckIn({ mood }), [addCheckIn]);

  const submitCheckIn = useCallback(
    async (input: NewCheckInInput): Promise<boolean> => {
      // Personalise the session immediately (in memory), but do not mark the
      // check-in satisfied or store a record until a save actually succeeds.
      setSession((s) => ({ ...s, mood: input.mood }));

      if (!saveHistoryRef.current || !email) {
        // History off: the check-in is satisfied with nothing stored (consent).
        persistSession({ checkedIn: true, mood: input.mood });
        return true;
      }

      const record = buildRecord(input);
      try {
        const ok = await moodService.createCheckIn(email, record);
        if (!ok) {
          flagRemoteError();
          return false; // a failed backend save is never reported as success
        }
        setCheckIns((prev) => {
          const next = [record, ...prev].slice(0, 2000);
          moodService.cacheCheckIns(email, next);
          return next;
        });
        persistSession({ checkedIn: true, mood: input.mood });
        setOffline(false);
        setError(null);
        return true;
      } catch {
        flagRemoteError();
        return false;
      }
    },
    [email, persistSession, flagRemoteError]
  );

  const updateCheckIn = useCallback(
    (id: string, patch: Partial<Omit<MoodCheckInRecord, 'id' | 'createdAt'>>) => {
      setCheckIns((prev) => {
        const next = prev.map((c) => (c.id === id ? { ...c, ...patch } : c));
        moodService.cacheCheckIns(email, next);
        return next;
      });
      moodService.updateCheckIn(email, id, patch).catch(flagRemoteError);
    },
    [email, flagRemoteError]
  );

  const deleteCheckIn = useCallback(
    (id: string) => {
      setCheckIns((prev) => {
        const next = prev.filter((c) => c.id !== id);
        moodService.cacheCheckIns(email, next);
        return next;
      });
      moodService.deleteCheckIn(email, id).catch(flagRemoteError);
    },
    [email, flagRemoteError]
  );

  const saveReflection = useCallback(
    (period: 'week' | 'month', periodKey: string, prompt: string, text: string) => {
      const trimmed = text.trim();
      setReflections((prev) => {
        const existing = prev.find((r) => r.period === period && r.periodKey === periodKey && r.prompt === prompt);
        let next: MoodReflection[];
        if (!trimmed) {
          next = prev.filter((r) => r !== existing);
          if (existing && email) moodService.deleteReflection(email, existing.id).catch(flagRemoteError);
        } else if (existing) {
          const updated = { ...existing, text: trimmed, updatedAt: new Date().toISOString() };
          next = prev.map((r) => (r === existing ? updated : r));
          if (email) moodService.saveReflection(email, updated, false).catch(flagRemoteError);
        } else if (saveHistoryRef.current) {
          const created: MoodReflection = {
            id: makeId('rf'),
            period,
            periodKey,
            prompt,
            text: trimmed,
            updatedAt: new Date().toISOString(),
          };
          next = [created, ...prev];
          if (email) moodService.saveReflection(email, created, true).catch(flagRemoteError);
        } else {
          next = prev; // consent: no new reflection stored when history is off
        }
        moodService.cacheReflections(email, next);
        return next;
      });
    },
    [email, flagRemoteError]
  );

  const deleteReflection = useCallback(
    (id: string) => {
      setReflections((prev) => {
        const next = prev.filter((r) => r.id !== id);
        moodService.cacheReflections(email, next);
        return next;
      });
      moodService.deleteReflection(email, id).catch(flagRemoteError);
    },
    [email, flagRemoteError]
  );

  const skipCheckIn = useCallback(() => {
    persistSession({ checkedIn: true, mood: session.mood });
  }, [persistSession, session.mood]);

  const updatePreferences = useCallback(
    (partial: Partial<MoodPreferences>) => {
      setPreferences((prev) => {
        const next = { ...prev, ...partial };
        if (email) writeJSON(localStorage, prefsKey(email), next);
        return next;
      });
    },
    [email]
  );

  const clearHistory = useCallback(() => {
    const previous = checkIns;
    setCheckIns([]);
    moodService.cacheCheckIns(email, []);
    moodService.clearCheckInsRemote(email, previous).catch(flagRemoteError);
  }, [email, checkIns, flagRemoteError]);

  const clearReflections = useCallback(() => {
    const previous = reflections;
    setReflections([]);
    moodService.cacheReflections(email, []);
    moodService.clearReflectionsRemote(email, previous).catch(flagRemoteError);
  }, [email, reflections, flagRemoteError]);

  const importLocalHistory = useCallback(async (): Promise<moodService.ImportResult> => {
    const result = await moodService.importLocalToBackend(email);
    if (result.failed === 0) setImported(true);
    // Reload from the backend so the imported rows appear with their server state.
    if (email) {
      const [ci, rf] = await Promise.all([moodService.loadCheckIns(email), moodService.loadReflections(email)]);
      setCheckIns(ci);
      setReflections(rf);
    }
    return result;
  }, [email]);

  const needsCheckIn =
    isAuthenticated && preferences.promptsEnabled && !preferences.skipOnOpen && !session.checkedIn && !!email;

  const localHistoryCount = useMemo(() => (email ? moodService.localHistoryCount(email) : 0), [email]);
  const canImportLocalHistory = backend && !imported && localHistoryCount > 0;

  const status: MoodSyncStatus = useMemo(
    () => ({ backend, loading, offline, error }),
    [backend, loading, offline, error]
  );

  const value = useMemo<MoodContextType>(
    () => ({
      sessionMood: session.mood,
      preferences,
      checkIns,
      reflections,
      needsCheckIn,
      status,
      chooseMood,
      submitCheckIn,
      addCheckIn,
      updateCheckIn,
      deleteCheckIn,
      saveReflection,
      deleteReflection,
      skipCheckIn,
      updatePreferences,
      clearHistory,
      clearReflections,
      localHistoryCount,
      canImportLocalHistory,
      importLocalHistory,
    }),
    [session.mood, preferences, checkIns, reflections, needsCheckIn, status, chooseMood, submitCheckIn, addCheckIn, updateCheckIn, deleteCheckIn, saveReflection, deleteReflection, skipCheckIn, updatePreferences, clearHistory, clearReflections, localHistoryCount, canImportLocalHistory, importLocalHistory]
  );

  return <MoodContext.Provider value={value}>{children}</MoodContext.Provider>;
};

export const useMood = (): MoodContextType => {
  const ctx = useContext(MoodContext);
  if (!ctx) throw new Error('useMood must be used within a MoodProvider');
  return ctx;
};
